/**
 * What the room form offers when its address already exists (409
 * `address_exists`, plan room-houses-and-combining-rooms §4 row 2; PO
 * 2026-10-06, points 6.1/6.2).
 *
 * One address should be one location: a building with a key locker in the
 * entrance and rooms upstairs is one location holding a station and a house.
 * So the form steers to the house, in this order:
 *
 *  - **houses**: a house stands there — choose it, the room goes in.
 *  - **create_house**: a location without a house — create a house there and
 *    put the room in it. A location holding only a locker is preferred; where
 *    a room already has it as its own location, that room is assigned to the
 *    new house afterwards ("Dem Haus zuordnen" adopts it: nothing moves).
 *  - **unknown**: an older backend that names no locations — the dialog only
 *    says the address exists.
 *
 * A second location at the same address stays possible in every mode, as the
 * exception it is.
 */

export interface ConflictHouse {
    id: string;
    name: string;
}

export interface ConflictLocation {
    id: string;
    name: string | null;
    hasHouse: boolean;
    hasRooms: boolean;
}

export interface AddressConflict {
    houses: ConflictHouse[];
    /** Absent from an older backend. */
    locations?: ConflictLocation[];
}

export type AddressConflictMode =
    | { mode: 'houses', houses: ConflictHouse[] }
    | { mode: 'create_house', location: ConflictLocation }
    | { mode: 'unknown' };

export const addressConflictMode = (conflict: AddressConflict): AddressConflictMode => {
    if (conflict.houses.length > 0) return { mode: 'houses', houses: conflict.houses };
    const withoutHouse = (conflict.locations ?? []).filter((location) => !location.hasHouse);
    const location = withoutHouse.find((candidate) => !candidate.hasRooms) ?? withoutHouse[0];
    return location ? { mode: 'create_house', location } : { mode: 'unknown' };
};

/** The conflict carried by a create error, or null when the error is something else. */
export const addressConflictOf = (err: unknown): AddressConflict | null => {
    const data = (err as { data?: { blockers?: string[] } & Partial<AddressConflict> })?.data;
    if (!data?.blockers?.includes('address_exists')) return null;
    return { houses: data.houses ?? [], ...(data.locations ? { locations: data.locations } : {}) };
};
